<script setup>
/**
 * One switch row inside a `SettingsSection` card.
 *
 * Switches apply immediately - they are not part of the page's dirty state and
 * never wait on the header's Save button. A row that cannot be changed on the
 * current plan renders `locked`, which keeps the setting visible (so people can
 * see it exists) while making it inert.
 */
import Switch from 'dashboard/components-next/switch/Switch.vue';

defineProps({
  label: { type: String, required: true },
  description: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  locked: { type: Boolean, default: false },
});

const modelValue = defineModel({ type: Boolean, default: false });
</script>

<template>
  <div class="flex flex-col gap-2 px-4 py-3.5 min-w-0">
    <div class="flex items-center justify-between gap-4 min-w-0">
      <div class="flex flex-col gap-0.5 min-w-0">
        <span class="text-sm truncate text-n-slate-12">{{ label }}</span>
        <span v-if="description" class="text-sm text-n-slate-11">
          {{ description }}
        </span>
      </div>
      <div class="flex items-center flex-shrink-0 gap-2">
        <slot name="trailing" />
        <span
          v-if="locked"
          class="i-ph-lock-simple size-4 text-n-slate-10"
          :title="$t('SETTINGS_KIT.LOCKED')"
        />
        <Switch v-else v-model="modelValue" :disabled="disabled" />
      </div>
    </div>
    <!-- Sub-options that only make sense while the row is on, e.g. a repeat
         checkbox under a notification toggle. -->
    <slot />
  </div>
</template>
